import { strings } from 'as-ui-kit/strings';
import { localOffset } from 'as-ui-kit/const/time';

// Берем сдвиг пришедшего времени в UTC относительно времени тайм-зоны
// кабинета так, чтобы запрашивая, например, date.getHours(), получали
// часы именно тайм-зоны из кабинета, а не локальной
export const utcToCurrent = utcTimeString => {
    const date = new Date(utcTimeString);
    return !isValidDate(date) ? null : date;
};

export const getTimeInLocalOffset = date => {
    const neededOffset = localOffset * 60 * 1000;
    return new Date(date.getTime() - neededOffset);
};

// Комбинирует дату 1-го параметра с временем 2-го параметра
export const combineDateAndTime = (date, time) => {
    const year = date.getFullYear(),
        month = date.getMonth(),
        day = date.getDate(),
        hour = time.getHours(),
        minutes = time.getMinutes();

    return new Date(year, month, day, hour, minutes, 0, 0);
};

export const addMinutes = (date, minutes) => {
    if (!isValidDate(date)) {
        return null;
    }
    return new Date(date.getTime() + minutes * 60000);
};

export const getLocalDate = () => {
    return new Date();
};

// for Loading/Unloading
// remark: "3:12", not "03:12"
export const getUTCTime = date => (date ? [date.getUTCHours(), addZeroLessTen(date.getUTCMinutes())].join(':') : null);

// 00 часов сегодня по времени кабинета (сегодняшняя полночь)
export const currentMidnight = () => new Date(utcToCurrent(new Date().setUTCHours(0, 0, 0, 0)).setHours(0, 0, 0, 0));

// 00 часов со сдвигом на количество дней
export const dayOffsetedFromTodayMidnight = days =>
    new Date(utcToCurrent(new Date().setUTCHours(0, 0, 0, 0)).setHours(24 * days, 0, 0, 0));

//Возвращает дату "28.09" или время "01:12" в зависимости сегодня или раньше
export const shortFormatDateTime = dateString => {
    const date = utcToCurrent(dateString);
    return date >= currentMidnight() ? simpleFormatTime(date) : simpleFormatDate(date, false);
};

// "13:17"
export const simpleFormatTime = date =>
    date ? [addZeroLessTen(date.getHours()), addZeroLessTen(date.getMinutes())].join(':') : null;

// "01.02.2001 или 01.02"
export const simpleFormatDate = (date, showYear = true, showShortYear) => {
    if (!date) {
        return null;
    }
    const res = [addZeroLessTen(date.getDate()), addZeroLessTen(date.getMonth() + 1)];

    if (showYear) {
        res.push(showShortYear ? date.getFullYear().toString().substring(2) : date.getFullYear());
    }

    return res.join('.');
};

// "13:17 01.02.2001"
export const simpleFormatDateTime = date => (date ? `${simpleFormatTime(date)} ${simpleFormatDate(date)}` : null);

export const isToday = date => {
    if (!isValidDate(date)) {
        throw new Error('first argument should be date');
    }

    const today = new Date();
    return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
};

const isTomorrow = date => {
    if (!isValidDate(date)) {
        throw new Error('first argument should be date');
    }

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
        date.getDate() === tomorrow.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
    );
};

/**
 * "сегодня, 12:00"
 * "11 ноя, 12:00"
 * "8 дек, 18:20"
 * @param date {Date}
 * @param locale {String} язык
 * @returns {string}
 */
export const getShortDateAndTime = (date, locale = 'ru') => {
    if (!isValidDate(date)) {
        throw new Error('first argument should be date');
    }

    const today = new Date();
    const isInThisYear = today.getFullYear() === date.getFullYear();
    const time = `${addZeroLessTen(date.getHours())}:${addZeroLessTen(date.getMinutes())}`;

    const isMoreThanThreeDaysAhead = date => {
        const threeDaysAhead = new Date(date);
        threeDaysAhead.setDate(threeDaysAhead.getDate() + 3);

        return date < threeDaysAhead;
    };

    if (isToday(date)) {
        return `${strings[locale].timeStrings.today}, ${time}`;
    } else if (isTomorrow(date)) {
        return `${strings[locale].timeStrings.tomorrow}, ${time}`;
    } else if (isMoreThanThreeDaysAhead(date) && !isInThisYear) {
        return `${simpleDayOfMonth(date, locale, true)} ${date.getFullYear()} ${strings[locale].timeStrings.year}`;
    } else {
        return `${simpleDayOfMonth(date, locale, true)}, ${time}`;
    }
};

/**
 * "сегодня в 01:12"
 * "вчера в 02:46"
 * "12 мар в 04:59"
 * "1 сен 2001 в 19:00"
 * @param date {Date}
 * @param locale {String} язык
 * @param withTime {Boolean} нужно ли выводить время
 * @returns {string}
 */
export const formatRelativeDate = (date, locale = 'ru', withTime = true) => {
    if (!isValidDate(date)) {
        return '';
    }

    const lastMidnight = new Date().setHours(0, 0, 0, 0);
    const lastDayMidnight = new Date().setHours(-24, 0, 0, 0);
    const currentYear = new Date().getFullYear();
    const lastYearMidnight = new Date(new Date().setHours(0, 0, 0, 0)).setYear(currentYear - 1);

    let datePrefix;
    if (date >= lastMidnight) {
        datePrefix = `${strings[locale].timeStrings.today}`;
        // вчера
    } else if (date < lastMidnight && date >= lastDayMidnight) {
        datePrefix = `${strings[locale].timeStrings.yesterday}`;
        // в течение года
    } else if (date < lastDayMidnight && date > lastYearMidnight) {
        datePrefix = `${simpleDayOfMonth(date, locale, true)}`;
    } else {
        datePrefix = `${simpleDayOfMonth(date, locale, true)} ${date.getFullYear()}`;
    }

    if (withTime) {
        return `${datePrefix} ${strings[locale].timeStrings.at} ${addZeroLessTen(date.getHours())}:${addZeroLessTen(
            date.getMinutes()
        )}`;
    } else {
        return datePrefix;
    }
};

export const isValidDate = testDate => {
    if (Object.prototype.toString.call(testDate) === '[object Date]') {
        return isNaN(testDate.getTime()) ? false : testDate;
    }

    return null;
};

export const shortWeekday = (date, locale = 'ru') => strings[locale].weekDaysLowerShortList[date.getDay()];

// "1 марта"
// "Jul, 19"
export function simpleDayOfMonth(date, locale = 'ru', useShortMonths) {
    const month = useShortMonths
        ? strings[locale].timeStrings.shortMonths[date.getMonth()]
        : strings[locale].timeStrings.months[date.getMonth()];

    if (locale === 'en') {
        return `${month}, ${date.getDate()}`;
    }

    return `${date.getDate()} ${month}`;
}

export function simpleDateRange(from, to, locale) {
    if (!isValidDate(from) || !isValidDate(to)) {
        return '';
    }

    const fromMonth = strings[locale].timeStrings.shortMonths[from.getMonth()];
    const toMonth = strings[locale].timeStrings.shortMonths[to.getMonth()];
    const fromDayOfWeek = strings[locale].weekDaysLowerShortList[from.getDay()];
    const toDayOfWeek = strings[locale].weekDaysLowerShortList[to.getDay()];
    const fromDate = from.getDate();
    const toDate = to.getDate();
    if (dateToISOWithoutTime(from) === dateToISOWithoutTime(to)) {
        return `${fromDate} ${fromMonth}, ${fromDayOfWeek}`;
    }

    if (from.getMonth() === to.getMonth()) {
        return `${fromDate} ${fromDayOfWeek} — ${toDate} ${toDayOfWeek}, ${fromMonth}`;
    }

    return `${fromDate} ${fromMonth}, ${fromDayOfWeek} — ${toDate} ${toMonth}, ${toDayOfWeek}`;
}

// "1.2.2001" -> "01.02.2001"
export const addZeroLessTen = value => (value < 10 ? '0' + value : value);

// Возвращает разницу с текущим временем кабинета в виде "00:00:00"
export const differenceFromNowInHMS = date =>
    getRemainingTimer(Math.abs(Math.round((date - getLocalDate().getTime()) / 1000)));

// "03:34:00"
// "00:00:12"
export const getRemainingTimer = secondsLeft => {
    const hours = Math.floor(secondsLeft / 3600);
    const minutes = Math.floor((secondsLeft - hours * 3600) / 60);
    const seconds = secondsLeft % 60;
    return addZeroLessTen(hours) + ':' + addZeroLessTen(minutes) + ':' + addZeroLessTen(seconds);
};

// "10 д, 2 ч"
// "1 ч, 5 мин"
export const formatNamedTime = (time, locale = 'ru', parts = 2) => {
    
    const { seconds, minutes, hours, days } = parseTimeString(time);

    const result = [];

    if (days > 0) {
        result.push(`${days}\u2009${strings[locale].timeStrings.d}`);
    }
    if (hours > 0) {
        result.push(`${hours}\u2009${strings[locale].timeStrings.h}`);
    }
    if (minutes > 0) {
        result.push(`${minutes}\u2009${strings[locale].timeStrings.min}`);
    }
    if (seconds >= 0) {
        result.push(`${seconds}\u2009${strings[locale].timeStrings.sec}`);
    }

    return result.slice(0, parts).join(', ');
};

/**
 * @typedef TimeParts
 * @property {number} seconds секунды
 * @property {number} minutes минуты
 * @property {number} hours часы
 * @property {number} days дни
 */

/**
 * Разеляет строку времени, записанную через `:` на состовляющие
 * @param timeString строка веремени
 * @returns {TimeParts}
 */
export const parseTimeString = timeString => {
    if (!timeString) {
        return null;
    }
    const timeParts = timeString.split(':');
    const seconds = parseInt(timeParts[2]);
    const minutes = parseInt(timeParts[1]);
    const wholeHours = parseInt(timeParts[0]);
    const hours = wholeHours % 24;
    const days = (wholeHours - hours) / 24;

    return { seconds, minutes, hours, days };
};

export const addLeadingZerosToTime = timeString => {
    if (!timeString) {
        return null;
    }
    const timeParts = parseTimeString(timeString);

    return `${addZeroLessTen(timeParts.hours)}:${addZeroLessTen(timeParts.minutes)}`;
};

export const cutTimeStringToMinutes = time => {
    const { seconds, minutes, hours, days } = parseTimeString(time);

    const newMinutes = minutes + hours * 60 + days * 24 * 60;

    return `${addZeroLessTen(newMinutes)}:${addZeroLessTen(seconds)}`;
};

// Возвращает объект Date со смещением на указанное ко-во часов вперед
export const decimalHoursFromSpecificTime = (hours, date) =>
    new Date(utcToCurrent(date).getTime() + hours * 3600 * 1000);

export const currentToday = () => new Date(new Date().setHours(12, 0, 0, 0));

export const simpleDate = (date, locale = 'ru', useShortMonths) => {
    const month = useShortMonths
        ? strings[locale].timeStrings.shortMonths[date.getMonth()]
        : strings[locale].timeStrings.months[date.getMonth()];

    return `${date.getDate()} ${month} ${date.getFullYear()}`;
};

// 10-10-2020 => new Date(2020, 10, 10, now.getHours(), now.getMinutes())
export const getISODateWithLocalTime = dateStr => {
    const [year, month, day] = dateStr.split('-').map(value => parseInt(value));
    const now = new Date();
    return new Date(year, month - 1, day, now.getHours(), now.getMinutes());
};

export const isInThePast = time => (time ? getLocalDate() > time : false);

export const isDateInThePast = date => (date ? currentToday() > new Date(date).setHours(12, 0, 0, 0) : false);

// "2017-04-17"
export const dateToISOWithoutTime = date => (date ? date.toISOString().split('T')[0] : null);

// "2017-04-17" возвращает дату, учитывая время и таймзону
export const dateToLocaleDateWithoutTime = date => translateDateStringToISO(simpleFormatDate(date));

// "2017-04-21T12:26:24.059Z" -> "21.04.2017"
export const translateDateStringToLocal = dateString => {
    if (!dateString) return;
    return dateString.split('T')[0].split('-').reverse().join('.');
};
// "2017-04-21T12:26:24.059Z" -> "12:26:24"
export const translateDateStringToLocalTime = dateString => dateString.split('T')[1].split('-').reverse().join('.');

// "21.04.2017" -> "2017-04-21"
export const translateDateStringToISO = dateString => dateString.split('.').reverse().join('-');

// "2017-04-21" -> "21.04.2017"
export const translateISOToDateString = dateString => (dateString ? dateString.split('-').reverse().join('.') : '');

// "2017-04-21T12:26:24.059Z" -> "2017-04-21"
export const dateStringToISOWithoutTime = dateString =>
    dateString ? dateToISOWithoutTime(new Date(dateString)) : null;

export const getTimestampFromISOString = dateString => (dateString ? new Date(dateString).getTime() : null);

export const getMinutesEarlierThanNow = minutes => {
    const now = getLocalDate();
    return new Date(now.setMinutes(now.getMinutes() - minutes));
};

export const getDayFromDate = date => (date ? new Date(date.setHours(12, 0, 0, 0)) : null);

// "2019-04-25" -> "2019-04-25T12:00:00+0300"
export const getISODateString = shortDateString => {
    const sign = localOffset < 0 ? '+' : '-';
    return new Date(
        `${shortDateString}T12:00:00${sign}${addZeroLessTen(Math.abs(Math.floor(localOffset / 60)))}${addZeroLessTen(
            Math.abs(Math.floor(localOffset % 60))
        )}`
    );
};

/**
 * Соединяет части времени в запись через `:`
 * @param parts {TimeParts}
 * @returns string
 */
export const joinTimeParts = parts => {
    const { seconds, minutes, hours, days } = parts;

    const result = [];

    if (!isNaN(days)) {
        result.push(days);
    }
    if (!isNaN(hours)) {
        result.push(addZeroLessTen(hours));
    }
    if (!isNaN(minutes)) {
        result.push(addZeroLessTen(minutes));
    }
    if (!isNaN(seconds)) {
        result.push(addZeroLessTen(seconds));
    }

    return result.join(':');
};
